import { getBoxInObjectSpace } from './objectSpaceGeometry';

// Approximate real-world material tile size used for object-space UV projection.
const DEFAULT_TEXTURE_TILE_INCHES = 72;
const HEIGHT_ORIGINAL_BOUNDS_TAG = 'tallCabinetHeightOriginalBounds';
const SIZE_ORIGINAL_BOUNDS_TAG = 'tallCabinetSizeOriginalBounds';
// Door/drawer faces keep their authored UVs except for the door center panel handled by frontLineNineSliceResizer.
const DEFAULT_PROJECTED_MESH_NAME = /^(?!(Door[ _](Left|Right)|Drawer|Shelf|Handle_))/;

function hasProjectedTexture(mesh, options) {
  if (options.meshNamePattern && !options.meshNamePattern.test(mesh.name || '')) {
    return false;
  }

  const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  const texturedMaterials = materials.filter((material) => material && material.map);

  if (options.targetTexture) {
    return texturedMaterials.some((material) => material.map === options.targetTexture);
  }

  return texturedMaterials.length > 0;
}

function getDominantTexture(instance) {
  const textureCounts = new Map();

  instance.traverse((child) => {
    if (!child.isMesh) {
      return;
    }

    const materials = Array.isArray(child.material) ? child.material : [child.material];
    materials.forEach((material) => {
      if (material && material.map) {
        textureCounts.set(material.map, (textureCounts.get(material.map) || 0) + 1);
      }
    });
  });

  let dominantTexture = null;
  let dominantCount = 0;

  textureCounts.forEach((count, texture) => {
    if (count > dominantCount) {
      dominantTexture = texture;
      dominantCount = count;
    }
  });

  return dominantTexture;
}

function getDimensionRatio(boundsSize, cabinetSize) {
  const normalizedCabinetSize = Number(cabinetSize);

  if (!boundsSize || boundsSize <= 0 || !normalizedCabinetSize || normalizedCabinetSize <= 0) {
    return boundsSize || 1;
  }

  return boundsSize / normalizedCabinetSize;
}

function getOriginalDimensionRatio(instance, boundsTag, dimension, shape, shapeGetter, fallbackRatio) {
  const bounds = instance.userData[boundsTag];
  const shapeSize = shape && typeof shape[shapeGetter] === 'function' ? shape[shapeGetter]() : null;

  if (!bounds || !bounds[dimension]) {
    return fallbackRatio;
  }

  return getDimensionRatio(bounds[dimension], shapeSize) || fallbackRatio;
}

function getProjectionScale(instance, instanceBounds, shape, options) {
  const tileInches = options.textureTileInches || DEFAULT_TEXTURE_TILE_INCHES;
  const width = shape && typeof shape.getWidth === 'function' ? shape.getWidth() : null;
  const depth = shape && typeof shape.getDepth === 'function' ? shape.getDepth() : null;
  const height = shape && typeof shape.getHeight === 'function' ? shape.getHeight() : null;
  const currentWidthRatio = getDimensionRatio(instanceBounds.width, width);
  const currentHeightRatio = getDimensionRatio(instanceBounds.height, height);
  const currentDepthRatio = getDimensionRatio(instanceBounds.depth, depth || width);

  return {
    x: getOriginalDimensionRatio(
      instance,
      SIZE_ORIGINAL_BOUNDS_TAG,
      'width',
      shape,
      'getInitialWidth',
      currentWidthRatio
    ) * tileInches,
    y: getOriginalDimensionRatio(
      instance,
      HEIGHT_ORIGINAL_BOUNDS_TAG,
      'height',
      shape,
      'getInitialHeight',
      currentHeightRatio
    ) * tileInches,
    z: getOriginalDimensionRatio(
      instance,
      SIZE_ORIGINAL_BOUNDS_TAG,
      'depth',
      shape,
      'getInitialDepth',
      currentDepthRatio
    ) * tileInches,
  };
}

// Some GLBs rely on material_url textures but do not always provide usable UVs after procedural resizing.
function ensureUvAttribute(geometry, vertexCount) {
  if (geometry.attributes.uv && geometry.attributes.uv.array) {
    return geometry.attributes.uv;
  }

  const THREE = window.THREE;
  const uv = new THREE.BufferAttribute(new Float32Array(vertexCount * 2), 2);
  geometry.setAttribute('uv', uv);
  return uv;
}

function getInstanceLocalNormal(mesh, instance, localNormal, helper) {
  helper.localOrigin.set(0, 0, 0);
  helper.localNormalPoint.copy(localNormal).normalize();

  mesh.localToWorld(helper.localOrigin);
  mesh.localToWorld(helper.localNormalPoint);
  instance.worldToLocal(helper.localOrigin);
  instance.worldToLocal(helper.localNormalPoint);

  return helper.localNormalPoint.sub(helper.localOrigin).normalize();
}

// Projects UVs by dominant face normal so top, side, and front/back surfaces keep independent grain directions.
function setProjectedUv(instanceLocalVertex, instanceLocalNormal, scale, uvArray, uvIndex) {
  const absX = Math.abs(instanceLocalNormal.x);
  const absY = Math.abs(instanceLocalNormal.y);
  const absZ = Math.abs(instanceLocalNormal.z);

  if (absY >= absX && absY >= absZ) {
    uvArray[uvIndex] = instanceLocalVertex.x / scale.x;
    uvArray[uvIndex + 1] = instanceLocalVertex.z / scale.z;
    return;
  }

  if (absX >= absZ) {
    uvArray[uvIndex] = instanceLocalVertex.y / scale.y;
    uvArray[uvIndex + 1] = instanceLocalVertex.z / scale.z;
    return;
  }

  uvArray[uvIndex] = instanceLocalVertex.y / scale.y;
  uvArray[uvIndex + 1] = instanceLocalVertex.x / scale.x;
}

function updateTextureWrapping(material) {
  const materials = Array.isArray(material) ? material : [material];

  materials.forEach((sourceMaterial) => {
    if (!sourceMaterial || !sourceMaterial.map) {
      return;
    }

    sourceMaterial.map.wrapS = window.THREE.RepeatWrapping;
    sourceMaterial.map.wrapT = window.THREE.RepeatWrapping;
    sourceMaterial.map.needsUpdate = true;
    sourceMaterial.needsUpdate = true;
  });
}

function projectMeshUv(mesh, instance, scale, options) {
  if (!mesh.geometry || !mesh.geometry.attributes || !mesh.geometry.attributes.position) {
    return false;
  }

  if (!hasProjectedTexture(mesh, options)) {
    return false;
  }

  const THREE = window.THREE;
  const position = mesh.geometry.attributes.position;
  const normal = mesh.geometry.attributes.normal;
  const uv = ensureUvAttribute(mesh.geometry, position.count);
  const helper = {
    localVertex: new THREE.Vector3(),
    localNormal: new THREE.Vector3(),
    localOrigin: new THREE.Vector3(),
    localNormalPoint: new THREE.Vector3(),
    instanceLocalVertex: new THREE.Vector3(),
    instanceLocalNormal: new THREE.Vector3(),
  };

  mesh.updateMatrixWorld(true);

  for (let vertexIndex = 0; vertexIndex < position.count; vertexIndex += 1) {
    helper.localVertex.fromBufferAttribute(position, vertexIndex);
    mesh.localToWorld(helper.localVertex);
    instance.worldToLocal(helper.localVertex);
    helper.instanceLocalVertex.copy(helper.localVertex);

    if (normal) {
      helper.localNormal.fromBufferAttribute(normal, vertexIndex);
      helper.instanceLocalNormal.copy(getInstanceLocalNormal(mesh, instance, helper.localNormal, helper));
    } else {
      helper.instanceLocalNormal.set(0, 1, 0);
    }

    setProjectedUv(helper.instanceLocalVertex, helper.instanceLocalNormal, scale, uv.array, vertexIndex * 2);
  }

  uv.needsUpdate = true;
  updateTextureWrapping(mesh.material);
  return true;
}

// Rebuilds material UVs from object-space coordinates so resized cabinet box faces do not stretch their texture.
export function applyCabinetMaterialUvProjection(instance, shape, options = {}) {
  if (!instance || !shape) {
    return;
  }

  instance.updateMatrixWorld(true);
  const box = getBoxInObjectSpace(instance, instance);
  const instanceBounds = {
    width: box.max.x - box.min.x,
    height: box.max.y - box.min.y,
    depth: box.max.z - box.min.z,
  };
  const scale = getProjectionScale(instance, instanceBounds, shape, options);
  const projectionOptions = {
    ...options,
    meshNamePattern: options.meshNamePattern || DEFAULT_PROJECTED_MESH_NAME,
    targetTexture: options.useDominantTexture
      ? getDominantTexture(instance)
      : options.targetTexture,
  };
  instance.traverse((child) => {
    if (child.isMesh) {
      projectMeshUv(child, instance, scale, projectionOptions);
    }
  });
}
